import {
  ActionIcon,
  Divider,
  Flex,
  List,
  Text,
  useMantineTheme,
} from "@mantine/core";
import { useModals } from "@mantine/modals";
import { IconPlaylistAdd } from "@tabler/icons";
import { useEffect, useState } from "react";
import ActionCard from "src/components/ActionCard";
import { CardRadius, EMPLOYEE_ROLE_OBJ, HeightCard } from "src/utils/constants";
import CreateEditNotificationModal from "./CreateEditModal";
import {
  GetSendgridTemplateManyQuery,
  useGetSendgridTemplateManyLazyQuery,
  useDeleteSendgridTemplateMutation,
  useUpdateSendgridTemplateMutation,
} from "src/generated/graphql";
import useWritePermission from "src/hooks/usePermission";
import useTransitionMenu from "src/hooks/useTransitionMenu";

interface NotificationsListProps {
  locId: string;
}

const NotificationsList = ({ locId }: NotificationsListProps) => {
  const { TransitionMenu, setActiveOption, ShowActivation } =
    useTransitionMenu();
  const modals = useModals();
  const theme = useMantineTheme();
  const isAllowWrite = useWritePermission(EMPLOYEE_ROLE_OBJ.orgLocations);
  const [notifications, setNotifications] = useState<
    GetSendgridTemplateManyQuery["sendgridTemplateMany"]
  >([]);
  let color = theme.colors.secondaryTextColor[6];

  const [getSendGridNotifications] = useGetSendgridTemplateManyLazyQuery({
    onCompleted: (data) => {
      setNotifications(data.sendgridTemplateMany || []);
    },
    fetchPolicy: "network-only",
  });

  const [deleteSendGridNotification] = useDeleteSendgridTemplateMutation({
    onCompleted: () => {
      getSendGridTemplateNotifications();
    },
    fetchPolicy: "network-only",
  });

  const getSendGridTemplateNotifications = () => {
    if (locId) {
      getSendGridNotifications({
        variables: {
          id: locId,
          includeAll: true,
        },
      });
    }
  };
  useEffect(() => {
    getSendGridTemplateNotifications();
  }, [locId]);

  const handleOpenListMenu = (option: any) => {
    setActiveOption(option);
  };

  const addActionIcon = (
    <ActionIcon size="lg" onClick={() => onCreate()}>
      <IconPlaylistAdd />
    </ActionIcon>
  );

  const onCreate = () => {
    modals.openModal({
      closeOnClickOutside: false,
      title: "Create Notification",
      children: (
        <CreateEditNotificationModal
          locId={locId}
          isEdit={false}
          onRefetch={() => getSendGridTemplateNotifications()}
          onClose={() => modals.closeAll()}
        />
      ),
    });
  };

  const onEdit = (id: string) => {
    modals.openModal({
      closeOnClickOutside: false,
      title: "Edit Notification",
      children: (
        <CreateEditNotificationModal
          itemId={id}
          locId={locId}
          isEdit={true}
          onRefetch={getSendGridTemplateNotifications}
          onClose={() => modals.closeAll()}
        />
      ),
    });
  };

  const onDelete = (id: string) => {
    deleteSendGridNotification({ variables: { id } });
  };

  const [UpdateSendGridTemplate] = useUpdateSendgridTemplateMutation({
    onCompleted: () => {
      getSendGridTemplateNotifications();
    },
  });
  const onUpdateActivation = (id: string, isActive: boolean) => {
    UpdateSendGridTemplate({ variables: { id, data: { isActive } } });
  };

  return (
    <ActionCard
      title="Notifications"
      rightIconButton={isAllowWrite ? addActionIcon : null}
      height={HeightCard.md}
      radius={CardRadius.md}
      size={"md"}
      overflow="hidden"
    >
      <List w="100%" listStyleType="none" style={{ color }}>
        {notifications?.map((item, index) => (
          <List.Item
            w="100%"
            icon={null}
            key={item.id}
            c={color}
            sx={{
              "&": {
                position: "relative",
              },
              "& ::marker": {
                content: "none",
              },
              "& .mantine-List-itemWrapper": {
                display: "flex",
              },
              "& .mantine-List-itemWrapper > span > div": {
                alignItems: "center",
              },
            }}
          >
            {!!index && (
              <Divider style={{ borderColor: theme.colors.gray[2] }} />
            )}
            <Flex
              align="center"
              justify="space-between"
              sx={{
                padding: "4px 0",
              }}
            >
              <Text size={16} sx={{ width: "200px" }}>
                {item.trigger_}
              </Text>

              <Text fw={500} sx={{ width: "150px" }}>
                {item.type}
              </Text>

              {ShowActivation(item.isActive)}

              {isAllowWrite && (
                <TransitionMenu
                  onClick={() => handleOpenListMenu(item)}
                  onEdit={() => onEdit(item.id)}
                  onDelete={onDelete}
                  id={item.id}
                  isActive={item.isActive}
                  onUpdateActivation={onUpdateActivation}
                />
              )}
            </Flex>
          </List.Item>
        ))}
      </List>
    </ActionCard>
  );
};

export default NotificationsList;
